import React from "react";
import { Dialog, Box, Typography, IconButton, Button, CircularProgress, Divider, Tabs, Tab } from "@mui/material";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, Link, Link2Off, Database } from "lucide-react";
import DescriptionMatchSettingsMobile from "./description-match-settings-mobile";
import TextToggle from "../../ui/text-toggle";
import BottomSheetDialog from "./mobile-bottom-sheet";

import useApi from "../../../hooks/use-api";
import buildApiRequestConfig from "../../../api/url-builder";
import routes from "../../../api/routes";
import { useSettings } from "../../../providers/settings-provider";
import { usePermissions } from "../../../providers/permissions-provider";
import { useUser } from "../../../providers/user-provider";
import DeletePopupMobile from "./delete-mobile";
import LinkTransactionPopupMobile from "./link-transaction-mobile";
import TreeDropdown from "../../tree-dropdown";
import { useToast } from "../../../providers/toaster-provider";
import trxImage from '../../../files/images/transaction.png';
import AddProvisionPopup from "../add-provision";
import ProvisionTransactionsDisplay from "./provision-transactions-display-mobile";

// Bottom-sheet phone equivalent of transaction-detail.tsx. Deliberately a
// separate component rather than an isMobile branch inside that file - the
// desktop version leans entirely on MaterialReactTable (virtualized,
// multi-column, hover tooltips) which doesn't compact down to a phone width
// in any reasonable way. This reimplements the same data/actions with card
// lists instead of tables. State/effects below are intentionally a close
// port of transaction-detail.tsx's own (same API routes, same response
// shapes, same edge-case flags) - keep the two in sync by hand if the
// underlying transaction/provision-linking behavior changes.

// helper used when provisioning a transaction that isn't recognized as recurring
const adjustStartDate = (dateStr?: string, analysis?: any): string | undefined => {
    if (!dateStr) return dateStr;
    const interval = analysis?.interval?.toString()?.toLowerCase();
    if (interval === 'unknown' || interval === 'not recurring' || interval === 'none') {
        const d = new Date(dateStr);
        d.setMonth(d.getMonth() + 1);
        return d.toISOString().split('T')[0];
    }
    return dateStr;
};

interface TransactionDetailMobileProps {
    open: boolean;
    transactionId: string | null;
    invoiceNumber?: string | null;
    onClose: () => void;
    companyStatus: {
        lowestAmount: number | null;
        highestAmount: number | null;
    };
    onLink?: () => void;
    autoOpenLinkModal?: boolean;
    onAutoOpenLinkModalClose?: () => void;
    hierarchicalCategories?: { id: string; label: string; children?: any[] }[];
}

// One row in the History/Related card lists - same fields the desktop
// table's columns read off each record, kept flat here instead of a
// column-def array since a card just renders all of them at once.
type DetailRow = {
    guid: string;
    type: 'Provision' | 'Import';
    date: string;
    dateLinked?: string | null;
    amount: number;
    originalAmount?: number;
    remainingBalance?: number;
    description: string;
    category?: string;
    bank?: string;
    linkedTransactionId?: string | null;
};

const FieldBlock: React.FC<{ label: string; value: React.ReactNode; color?: string }> = ({ label, value, color }) => (
    <Box>
        <Typography sx={{ fontSize: 11, fontWeight: 500, color: '#707070', textTransform: 'uppercase' }}>{label}</Typography>
        <Typography sx={{ fontSize: 14, fontWeight: 600, color: color || 'var(--color-default-text)' }}>{value}</Typography>
    </Box>
);

const TransactionDetailMobile: React.FC<TransactionDetailMobileProps> = ({
    open,
    transactionId,
    invoiceNumber,
    onClose,
    companyStatus,
    onLink,
    autoOpenLinkModal,
    onAutoOpenLinkModalClose,
    hierarchicalCategories,
}) => {
    const navigate = useNavigate();
    const processedAnalysisRef = React.useRef<any>(null);
    const processedCreateProvisionRef = React.useRef<number | null>(null);
    const processedMarkTrxRef = React.useRef<any>(null);

    const { formatDate, formatAmount } = useSettings();
    const { can } = usePermissions();
    const { user } = useUser();
    const { showError, showSuccess } = useToast();
    const [data, setData] = React.useState<any>(null);
    const hasLinkedTransactions = (data?.linkedTransactions || []).some((trx: any) => trx.guid !== data?.transaction?.guid);
    const isPaidAtImportProvision = data?.transaction?.type === 'provision' && data?.transaction?.sourceData?.paidAtImport === true && !hasLinkedTransactions;
    const isUnlinked = !!data?.transaction && !hasLinkedTransactions;
    const isViewingProvision = data?.transaction?.type === 'provision' && !isPaidAtImportProvision;
    const [tabValue, setTabValue] = React.useState<number>(0);
    const [relatedTransactions, setRelatedTransactions] = React.useState<any[]>([]);
    const [openModal, setOpenModal] = React.useState<string>('');
    const [unlinkingTransactionId, setUnlinkingTransactionId] = React.useState<string | null>(null);
    const [isLinkable, setIsLinkable] = React.useState<boolean>(false);
    const [provisionThisData, setProvisionThisData] = React.useState<{ date?: string; amount?: number | string; description?: string } | null>(null);
    const [intervalAnalysis, setIntervalAnalysis] = React.useState<any>(null);
    const [patternAnalysisMessage, setPatternAnalysisMessage] = React.useState<string>('');
    const [showPatternConfirmation, setShowPatternConfirmation] = React.useState(false);

    const [showMatchSettings, setShowMatchSettings] = React.useState(false);
    const [pendingDescriptionGuid, setPendingDescriptionGuid] = React.useState<string | null>(null);
    const [pendingDescriptionText, setPendingDescriptionText] = React.useState<string>('');
    const [descriptionOptions, setDescriptionOptions] = React.useState<{ start: number; length: number } | null>(null);
    const [isDescriptionOnlyMatch, setIsDescriptionOnlyMatch] = React.useState(false);
    const [selectedLinkedTransactionId, setSelectedLinkedTransactionId] = React.useState<string | null>(null);
    const [showProvisionTransactions, setShowProvisionTransactions] = React.useState(false);
    const [provisionTransactionsToConfirm, setProvisionTransactionsToConfirm] = React.useState<any[]>([]);
    const [createdProvisionIdForMark, setCreatedProvisionIdForMark] = React.useState<number | null>(null);

    const [fetch, setFetch] = useApi();
    const [link, setLink] = useApi();
    const [unlink, setUnlink] = useApi();
    const [updateCategory, setUpdateCategory] = useApi();
    const [createProvision, setCreateProvision] = useApi();
    const [analyzePattern, setAnalyzePattern] = useApi();
    const [fetchRelated, setFetchRelated] = useApi();
    const [fetchPatternAnalysis, setFetchPatternAnalysis] = useApi();
    const [markTrx, setMarkTrx] = useApi();
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const [toggleLinkable, setToggleLinkable] = useApi();

    const patternAnalysis = fetchPatternAnalysis.data?.patternAnalysisSnapshot || null;
    const patternAnalysisSourceTransaction = patternAnalysis?.transactions?.find((t: any) => t.isSource) || null;
    const patternAnalysisMatches = patternAnalysis?.transactions?.filter((t: any) => !t.isSource) || [];

    const reanalyzeWithDescriptionOnly = (sourceTrxGuid: string, start?: number, length?: number) => {
        let { url, method } = buildApiRequestConfig(routes.provisionTemplate.analyzePatternByDescription, [{ param: 'transactionGuid', value: sourceTrxGuid }]);
        const qp: Record<string, any> = {};
        if (start !== undefined) qp.start = start;
        if (length !== undefined) qp.length = length;
        if (Object.keys(qp).length > 0) {
            const query = new URLSearchParams(qp).toString();
            url = `${url}?${query}`;
        }
        setAnalyzePattern({ url, method });
    };

    React.useEffect(() => {
        if (!open) return;
        const fetchId = invoiceNumber || transactionId;
        if (!fetchId) return;

        let params = [{ param: 'guid', value: transactionId }];
        if (invoiceNumber) {
            params.push({ param: 'invoiceNumber', value: invoiceNumber });
            params.push({ param: 'entityId', value: user?.entityId });
        }
        let { url, method } = buildApiRequestConfig(routes.transaction.fetchByGuid, params);
        setFetch({ url, method });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [transactionId, invoiceNumber, open, user?.entityId]);

    React.useEffect(() => {
        if (tabValue === 1 && transactionId && open) {
            const { url, method } = buildApiRequestConfig(routes.transaction.fetchRelated, [{ param: 'guid', value: transactionId }]);
            setFetchRelated({ url, method });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [tabValue, transactionId, open, data?.transaction?.type]);

    React.useEffect(() => {
        const provisionTemplateId = data?.transaction?.provisionTemplateId;
        if (tabValue === 2 && provisionTemplateId && open) {
            const { url, method } = buildApiRequestConfig(routes.provisionTemplate.fetchTransactionsById, [{ param: 'id', value: provisionTemplateId }]);
            setFetchPatternAnalysis({ url, method });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [tabValue, data?.transaction?.provisionTemplateId, open]);

    React.useEffect(() => {
        if (autoOpenLinkModal && openModal !== 'link') {
            setOpenModal('link');
            onAutoOpenLinkModalClose?.();
        }
    }, [autoOpenLinkModal, open, openModal, onAutoOpenLinkModalClose]);

    const refetch = React.useCallback(() => {
        if (!transactionId || !open) return;
        let params = [{ param: 'guid', value: transactionId }];
        if (invoiceNumber) params.push({ param: 'invoiceNumber', value: invoiceNumber });
        const { url, method } = buildApiRequestConfig(routes.transaction.fetchByGuid, params);
        setFetch({ url, method });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [transactionId, invoiceNumber, open]);

    React.useEffect(() => {
        const { data, isLoading, error } = link;
        if (data && !isLoading) {
            showSuccess("Transactions linked successfully");
            setOpenModal('');
            onLink?.();
            setLink(null);
            refetch();
        }
        if (error) {
            showError(error ? error.toString() : "Failed to link transactions");
            setLink(null);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [link.data, link.isLoading, link.error]);

    React.useEffect(() => {
        const { data, isLoading, error } = unlink;
        if (data && !isLoading) {
            showSuccess("Transactions unlinked successfully");
            setOpenModal('');
            onLink?.();
            setUnlink(null);
            refetch();
        }
        if (error) {
            showError(error ? error.toString() : "Failed to unlink transactions");
            setUnlink(null);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [unlink.data, unlink.isLoading, unlink.error]);

    React.useEffect(() => {
        const { data, isLoading, error } = updateCategory;
        if (data && !isLoading) {
            showSuccess("Category updated successfully");
            setUpdateCategory(null);
            onLink?.();
            refetch();
        }
        if (error) {
            showError(error ? error.toString() : "Failed to update category");
            setUpdateCategory(null);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [updateCategory.data, updateCategory.isLoading, updateCategory.error]);

    React.useEffect(() => {
        const { data, isLoading, error } = createProvision;
        if (data && !isLoading && data?.data?.id && processedCreateProvisionRef.current !== data.data.id) {
            processedCreateProvisionRef.current = data.data.id;
            showSuccess("Provision created successfully");
            setOpenModal('');
            setDescriptionOptions(null);
            setIsDescriptionOnlyMatch(false);
            onLink?.();

            if (Array.isArray(data.transactionsToMark) && data.transactionsToMark.length > 0) {
                setCreatedProvisionIdForMark(data.data.id);
                setProvisionTransactionsToConfirm(data.transactionsToMark);
                setShowProvisionTransactions(true);
            }

            setCreateProvision(null);
            refetch();
        }
        if (error) {
            showError(error ? error.toString() : "Failed to create provision");
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [createProvision.data, createProvision.isLoading, createProvision.error]);

    React.useEffect(() => {
        const { data, isLoading, error } = markTrx;
        if (data && !isLoading && processedMarkTrxRef.current !== data) {
            processedMarkTrxRef.current = data;
            showSuccess("Transactions marked as provisioned successfully");
            setShowProvisionTransactions(false);
            setCreatedProvisionIdForMark(null);
            setProvisionTransactionsToConfirm([]);
            setMarkTrx(null);
        }
        if (error) {
            showError(error ? error.toString() : "Failed to mark transactions");
            setMarkTrx(null);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [markTrx.data, markTrx.isLoading, markTrx.error]);

    React.useEffect(() => {
        const { data, isLoading } = fetch;
        if (data && !isLoading) {
            const linkedTrx = (data.linkedTransactions || []).map((trx: any) => ({
                ...trx,
                type: trx.type === 'provision' ? 'Provision' : 'Import',
            }));
            data.linkedTransactions = linkedTrx;
            setData(data);
            setIsLinkable(data.transaction?.isLinkable ?? false);
        }
    }, [fetch]);

    React.useEffect(() => {
        const { data, isLoading } = fetchRelated;
        if (data && !isLoading) {
            const relatedTrx = (data.relatedTransactions || [])
                .map((trx: any) => ({
                    ...trx,
                    type: trx.type === 'provision' ? 'Provision' : 'Import',
                    bank: trx.bankAccount?.accountName || '-',
                    category: trx.category?.name || '-',
                }))
                .sort((a: any, b: any) => new Date(b.date).getTime() - new Date(a.date).getTime());
            setRelatedTransactions(relatedTrx);
        }
    }, [fetchRelated]);

    React.useEffect(() => {
        const { data, isLoading, error } = analyzePattern;
        if (data && !isLoading) {
            if (processedAnalysisRef.current === data) return;
            processedAnalysisRef.current = data;

            if (!data.hasMatches) {
                if (!pendingDescriptionGuid) {
                    setPendingDescriptionGuid(data.sourceTransaction?.guid || null);
                    setPendingDescriptionText(data.sourceTransaction?.description || '');
                    setShowMatchSettings(true);
                } else {
                    setPatternAnalysisMessage(data.analysis?.message || "Unable to analyze pattern. Please fill in the details manually.");
                    setShowPatternConfirmation(true);
                    const sourceTransaction = data.sourceTransaction || {};
                    setProvisionThisData({
                        date: adjustStartDate(sourceTransaction.date, data.analysis),
                        amount: sourceTransaction.amount,
                        description: sourceTransaction.description
                    });
                    if (data.analysis) setIntervalAnalysis(data.analysis);
                    setPendingDescriptionGuid(null);
                }
            } else {
                const sourceTransaction = data.sourceTransaction || {};
                setProvisionThisData({
                    date: adjustStartDate(data.analysis?.startDate || sourceTransaction.date, data.analysis),
                    amount: sourceTransaction.amount,
                    description: sourceTransaction.description
                });
                if (data.analysis) setIntervalAnalysis(data.analysis);
                setPendingDescriptionGuid(null);
                setOpenModal('add-provision');
            }
            setAnalyzePattern(null);
        } else if (error && !isLoading) {
            if (processedAnalysisRef.current === error) return;
            processedAnalysisRef.current = error;
            showError("Failed to analyze pattern. Using transaction data instead.");
            setIntervalAnalysis(null);
            setOpenModal('add-provision');
            setAnalyzePattern(null);
        }
    }, [analyzePattern, showError, setAnalyzePattern, pendingDescriptionGuid]);

    const handleLinkTransactions = (transactionIds: string[]) => {
        const { url, method } = buildApiRequestConfig(routes.transaction.link);
        setLink({ url, method, body: { linkedTransactionId: transactionId, transactionIds } });
    };

    const handleCreateProvisionFromTransaction = (newProvision: any) => {
        const { url, method } = buildApiRequestConfig(routes.provisionTemplate.create);
        const body: any = {
            ...newProvision,
            entityId: data?.transaction?.entityId,
            appliedTaxBreakdown: JSON.stringify(newProvision.appliedTaxBreakdown),
            isDescriptionOnlyMatch,
            provisionThisSourceGuid: transactionId
        };
        if (descriptionOptions) body.descriptionOptions = descriptionOptions;
        setCreateProvision({ url, method, body });
    };

    const handleUnlinkTransaction = (targetGuid: string) => {
        if (!data?.transaction?.guid) return;
        const { url, method } = buildApiRequestConfig(routes.transaction.unlink, [{ param: 'guid', value: data?.transaction?.guid }]);
        setUnlink({ url, method, body: { targetTransactionGuid: targetGuid } });
    };

    const handleCategoryChange = (categoryId: number | null) => {
        if (!data?.transaction?.guid) return;
        const { url, method } = buildApiRequestConfig(routes.transaction.updateCategory, [{ param: 'guid', value: data.transaction.guid }]);
        setUpdateCategory({ url, method, body: { categoryId, applyDefaultTaxes: true } });
    };

    const onToggleStatus = () => {
        if (!transactionId) return;
        const { url, method } = buildApiRequestConfig(routes.transaction.toggleLinkable, [{ param: 'guid', value: transactionId }]);
        setToggleLinkable({ url, method });
        setIsLinkable(!isLinkable);
    };

    React.useEffect(() => {
        const { data, isLoading, error } = toggleLinkable;
        if (data && !isLoading) {
            onLink?.();
            setToggleLinkable(null);
        }
        if (error) {
            showError(error ? error.toString() : "Failed to update link status");
            setIsLinkable(prev => !prev);
            setToggleLinkable(null);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [toggleLinkable.data, toggleLinkable.isLoading, toggleLinkable.error]);

    const isRealTransaction = data?.transaction?.type === 'real';
    const isProvisionRelated = !!data?.transaction?.provisionThisTemplateId;

    let linkTooltipTitle = '';
    let linkButtonDisabled = !isLinkable || fetch.isLoading;
    if (isRealTransaction) {
        if (!isLinkable) {
            linkTooltipTitle = 'You cannot link any more provisions, as this transaction is already closed.';
        } else if (hasLinkedTransactions) {
            linkTooltipTitle = 'This transaction is already linked.';
            linkButtonDisabled = true;
        }
    } else if (hasLinkedTransactions) {
        linkTooltipTitle = 'This transaction is already linked';
    }

    const provisionThisDisabled = hasLinkedTransactions || isProvisionRelated;
    const provisionThisTooltipTitle = hasLinkedTransactions
        ? 'This transaction is linked to a provision.'
        : isProvisionRelated
            ? 'This transaction is related to a provision.'
            : '';

    const handleClose = () => { onClose(); setTabValue(0); };

    // Rows for the History tab - main record first (relabeled the same way
    // the desktop table does), then every other linked transaction.
    const historyRows: DetailRow[] = React.useMemo(() => {
        if (!data?.transaction || isUnlinked) return [];
        const linked = (data.linkedTransactions || []).filter((trx: any) => trx.guid !== data.transaction.guid);
        if (data.transaction.type === 'real' || isPaidAtImportProvision) {
            return [{ ...data.transaction, type: 'Import' }, ...linked];
        }
        return [
            {
                ...data.transaction,
                type: 'Provision',
                remainingBalance: (data.linkedTransactions || []).find((trx: any) => trx.guid === data.transaction.guid)?.remainingBalance,
            },
            ...linked,
        ];
    }, [data, isUnlinked, isPaidAtImportProvision]);

    const renderHistoryCard = (row: DetailRow, index: number) => {
        const isMain = index === 0;
        const clickable = !isMain && !!row.guid && row.guid !== transactionId;
        const shouldShowUnlink = data?.transaction?.type === 'real' || row.linkedTransactionId;
        const showUnlinkAction = shouldShowUnlink && row.guid !== transactionId;
        const color = row.type === 'Provision' ? 'var(--color-provision-text)' : 'var(--color-default-text)';
        return (
            <Box
                key={row.guid || index}
                onClick={() => clickable && setSelectedLinkedTransactionId(row.guid)}
                sx={{
                    p: 1.25,
                    borderRadius: 1,
                    border: '1px solid #e0e0e0',
                    background: 'var(--color-container-background)',
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 0.5,
                    cursor: clickable ? 'pointer' : 'default',
                }}
            >
                <Box display="flex" justifyContent="space-between" alignItems="flex-start">
                    <Box>
                        {(data?.transaction?.type === 'real' || isPaidAtImportProvision) && (
                            <Typography sx={{ fontSize: 10, fontWeight: 700, color, letterSpacing: 0.5 }}>{row.type.toUpperCase()}</Typography>
                        )}
                        <Typography sx={{ fontSize: 14, fontWeight: 600, color }}>{formatDate(new Date(row.date))}</Typography>
                        {row.dateLinked && (
                            <Typography sx={{ fontSize: 11, color: '#707070' }}>Linked {formatDate(new Date(row.dateLinked))}</Typography>
                        )}
                    </Box>
                    <Box display="flex" alignItems="center" gap={1}>
                        <Typography sx={{ fontSize: 14, fontWeight: 700, color }}>
                            {formatAmount?.(row.type === 'Provision' && row.originalAmount !== undefined ? row.originalAmount : row.amount)}
                        </Typography>
                        {showUnlinkAction && (
                            <IconButton
                                size="small"
                                onClick={(e) => {
                                    e.stopPropagation();
                                    setUnlinkingTransactionId(row.guid);
                                    setOpenModal('unlink');
                                }}
                                sx={{ p: 0.5 }}
                            >
                                <Link2Off size={16} color="#ff0000" />
                            </IconButton>
                        )}
                    </Box>
                </Box>
                <Typography sx={{ fontSize: 13, color }}>{row.description}</Typography>
                {isViewingProvision && row.remainingBalance !== undefined && (
                    <Typography sx={{ fontSize: 12, color }}>Remaining Balance: {formatAmount?.(row.remainingBalance)}</Typography>
                )}
            </Box>
        );
    };

    return (
        <BottomSheetDialog open={open} onClose={handleClose}>
            <Dialog
                open={analyzePattern.isLoading}
                maxWidth="sm"
                fullWidth
                PaperProps={{ sx: { borderRadius: 2, textAlign: 'center', padding: 3, boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)' } }}
            >
                <Box display="flex" flexDirection="column" alignItems="center" gap={2}>
                    <CircularProgress sx={{ color: 'var(--color-primary)' }} size={50} />
                    <Typography variant="h6" fontWeight={600}>Analyzing Transaction Pattern</Typography>
                    <Typography variant="body2" color="textSecondary">Detecting recurrence patterns and historical matches...</Typography>
                </Box>
            </Dialog>
            <AddProvisionPopup
                open={openModal === 'add-provision'}
                onClose={() => {
                    setOpenModal('detail');
                    setProvisionThisData(null);
                    setIntervalAnalysis(null);
                    setIsDescriptionOnlyMatch(false);
                    setDescriptionOptions(null);
                }}
                initialStartDate={provisionThisData?.date || data?.transaction?.date}
                initialAmount={provisionThisData?.amount ? String(provisionThisData.amount) : String(data?.transaction?.amount || '')}
                initialDescription={provisionThisData?.description || data?.transaction?.description}
                intervalAnalysis={intervalAnalysis}
                onCreate={handleCreateProvisionFromTransaction}
            />
            <ProvisionTransactionsDisplay
                open={showProvisionTransactions}
                provisionId={createdProvisionIdForMark}
                transactions={provisionTransactionsToConfirm}
                isLoading={markTrx.isLoading}
                onConfirm={(trxGuids: string[]) => {
                    if (!createdProvisionIdForMark) return;
                    const { url, method } = buildApiRequestConfig(routes.provisionTemplate.markTransactionAsProvisioned, [{ param: 'id', value: createdProvisionIdForMark }]);
                    setMarkTrx({ url, method, body: { transactionGuids: trxGuids } });
                }}
                onClose={() => {
                    setShowProvisionTransactions(false);
                    setCreatedProvisionIdForMark(null);
                    setProvisionTransactionsToConfirm([]);
                }}
            />
            <DeletePopupMobile
                open={showPatternConfirmation}
                message="Continue with Provision?"
                description={patternAnalysisMessage}
                onCancel={() => {
                    setShowPatternConfirmation(false);
                    setPatternAnalysisMessage('');
                    setProvisionThisData(null);
                    setIntervalAnalysis(null);
                }}
                onConfirm={() => {
                    setShowPatternConfirmation(false);
                    setOpenModal('add-provision');
                }}
            />
            <DescriptionMatchSettingsMobile
                open={showMatchSettings}
                sourceDescription={pendingDescriptionText}
                onClose={() => {
                    setShowMatchSettings(false);
                    setPendingDescriptionGuid(null);
                }}
                onConfirm={(start, length) => {
                    setShowMatchSettings(false);
                    setDescriptionOptions({ start, length });
                    setIsDescriptionOnlyMatch(true);
                    if (pendingDescriptionGuid) reanalyzeWithDescriptionOnly(pendingDescriptionGuid, start, length);
                }}
            />
            <DeletePopupMobile
                open={openModal === 'unlink'}
                message="Unlink this Transaction?"
                description="The transaction will remain in your records but will no longer be linked to this provision."
                onCancel={() => setOpenModal('')}
                image={{ src: trxImage, width: 150, height: 150 }}
                onConfirm={() => {
                    if (!unlinkingTransactionId) return;
                    handleUnlinkTransaction(unlinkingTransactionId);
                    setOpenModal('');
                }}
            />
            <LinkTransactionPopupMobile
                open={openModal === 'link'}
                onClose={() => {
                    setOpenModal('detail');
                    onAutoOpenLinkModalClose?.();
                }}
                onConfirm={(selectedTransactions) => {
                    if (selectedTransactions.length > 0) handleLinkTransactions(selectedTransactions);
                }}
                provisionAmount={data?.transaction?.amount || 0}
                isProvision={data?.transaction?.type === 'provision'}
                initialToleranceRange={{ min: companyStatus.lowestAmount || -9999, max: companyStatus.highestAmount || 9999 }}
                maxToleranceAmount={companyStatus.highestAmount ? Math.abs(companyStatus.highestAmount) : 10000}
                transactionBankId={data?.transaction?.bankId}
                sourceDescription={data?.transaction?.description}
            />
            {/* Drill-in to a linked/related row's own detail - same component, nested */}
            <TransactionDetailMobile
                open={!!selectedLinkedTransactionId}
                transactionId={selectedLinkedTransactionId}
                onClose={() => setSelectedLinkedTransactionId(null)}
                companyStatus={companyStatus}
                onLink={onLink}
                hierarchicalCategories={hierarchicalCategories}
            />

            {/* Header */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, px: 1, py: 1, borderBottom: '1px solid #e0e0e0', position: 'sticky', top: 0, background: 'var(--color-page-background)', zIndex: 2 }}>
                <IconButton onClick={handleClose}><ArrowLeft size={22} /></IconButton>
                <Typography sx={{ fontSize: 16, fontWeight: 700, flex: 1 }}>
                    {(data?.transaction?.type === 'provision' && !isPaidAtImportProvision) ? 'Provision Detail' : 'Transaction Detail'}
                </Typography>
                {!fetch.isLoading && data?.transaction && (
                    <Box
                        sx={{
                            px: 1.25, py: 0.5, borderRadius: 4,
                            background: (data.transaction.type === 'provision' && !isPaidAtImportProvision) ? 'var(--color-provision-text)' : 'var(--color-primary)',
                            color: '#fff', fontSize: 11, fontWeight: 700, display: 'flex', alignItems: 'center', gap: 0.5,
                        }}
                    >
                        <Database size={12} />
                        {(data.transaction.type === 'provision' && !isPaidAtImportProvision) ? 'Provision' : 'Imported'}
                    </Box>
                )}
            </Box>

            {fetch.isLoading ? (
                <Box display="flex" flexDirection="column" alignItems="center" justifyContent="center" flex={1} gap={2}>
                    <CircularProgress size={40} />
                    <Typography variant="h6" color="text.secondary">Loading transaction details...</Typography>
                </Box>
            ) : (
                <Box sx={{ flex: 1, overflowY: 'auto', display: 'flex', flexDirection: 'column' }}>
                    <Box sx={{ p: 2, display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                        {data?.transaction?.type === 'provision' && data?.transaction?.invoiceNumber && (
                            <Typography sx={{ fontSize: 13, fontStyle: 'italic' }}>
                                Generated from INVOICE #: <span style={{ textDecoration: 'underline', cursor: 'pointer', fontWeight: 600 }} onClick={() => navigate(`/invoices?invoiceNumber=${data?.transaction?.invoiceNumber}`)}>{data?.transaction?.invoiceNumber}</span>
                            </Typography>
                        )}
                        <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1.5, p: 1.5, border: '1px solid #e0e0e0', borderRadius: 1, background: 'var(--color-container-background)' }}>
                            <FieldBlock label="Date" value={formatDate(data?.transaction?.date || new Date())} color={isViewingProvision ? 'var(--color-provision-text)' : undefined} />
                            <FieldBlock label="Amount" value={formatAmount?.(data?.transaction?.amount)} color={isViewingProvision ? 'var(--color-provision-text)' : undefined} />
                            <Box sx={{ gridColumn: '1 / -1' }}>
                                <FieldBlock label="Description" value={data?.transaction?.description} color={isViewingProvision ? 'var(--color-provision-text)' : undefined} />
                            </Box>
                            <FieldBlock label="Bank" value={data?.transaction?.bank || '-'} color={isViewingProvision ? 'var(--color-provision-text)' : undefined} />
                            <Box>
                                <Typography sx={{ fontSize: 11, fontWeight: 500, color: '#707070', textTransform: 'uppercase', mb: 0.5 }}>Category</Typography>
                                <Box onClick={(e) => e.stopPropagation()}>
                                    <TreeDropdown
                                        enableHighlightMode
                                        options={(hierarchicalCategories || []).filter(cat => cat.id !== 'select-all-categories')}
                                        value={data?.transaction?.categoryId ? String(data.transaction.categoryId) : ''}
                                        onChange={(selectedValue) => handleCategoryChange(selectedValue ? Number(selectedValue) : null)}
                                        placeholder="Select..."
                                        height={36}
                                        width="100%"
                                        borderColor="var(--color-primary)"
                                        disabled={!can('transactions', 'update') || updateCategory.isLoading}
                                    />
                                </Box>
                            </Box>
                        </Box>
                        {(data?.transaction?.sourceData?.xeroDescription || data?.transaction?.sourceData?.overriddenByXero) && (
                            <Typography sx={{ fontSize: 11, fontStyle: 'italic', color: 'var(--color-text-muted)' }}>
                                {data?.transaction?.sourceData?.xeroDescription && <>Local description: {data?.transaction?.originalDescription || '-'}</>}
                                {data?.transaction?.sourceData?.xeroDescription && data?.transaction?.sourceData?.overriddenByXero && ' — '}
                                {data?.transaction?.sourceData?.overriddenByXero && (
                                    <>Originally: {data.transaction.sourceData.overriddenByXero.categoryName || '-'}{data.transaction.sourceData.overriddenByXero.overriddenAt ? ` (before Xero sync on ${formatDate(new Date(data.transaction.sourceData.overriddenByXero.overriddenAt))})` : ''}</>
                                )}
                            </Typography>
                        )}
                    </Box>

                    <Tabs
                        value={tabValue}
                        onChange={(_, v) => setTabValue(v)}
                        variant="fullWidth"
                        sx={{ borderBottom: 1, borderColor: 'divider', minHeight: 40, '& .MuiTab-root': { minHeight: 40, fontSize: 13 } }}
                    >
                        <Tab label="History" />
                        <Tab label="Related" />
                        {data?.transaction?.type === 'provision' && !!data?.transaction?.provisionTemplateId && (
                            <Tab label="Pattern" />
                        )}
                    </Tabs>

                    <Box sx={{ p: 2, display: 'flex', flexDirection: 'column', gap: 1, flex: 1 }}>
                        {tabValue === 0 && (
                            isUnlinked ? (
                                <Box display="flex" flexDirection="column" alignItems="center" justifyContent="center" gap={2} py={4}>
                                    <Typography color="text.secondary" textAlign="center">
                                        {data?.transaction?.type === 'provision'
                                            ? "This provision hasn't been linked to a transaction yet."
                                            : "This transaction hasn't been linked to a provision yet."}
                                    </Typography>
                                    <Button
                                        onClick={() => setOpenModal('link')}
                                        disabled={linkButtonDisabled}
                                        variant="outlined"
                                        sx={{ display: 'flex', gap: 1 }}
                                    >
                                        <Link size={16} />
                                        {data?.transaction?.type === 'provision' ? "Link this provision to a transaction" : "Link this transaction to a provision"}
                                    </Button>
                                </Box>
                            ) : historyRows.map((row, i) => renderHistoryCard(row, i))
                        )}

                        {tabValue === 1 && (
                            fetchRelated.isLoading ? (
                                <Box display="flex" flexDirection="column" alignItems="center" justifyContent="center" gap={2} py={4}>
                                    <CircularProgress size={40} />
                                    <Typography color="text.secondary">Loading related transactions...</Typography>
                                </Box>
                            ) : relatedTransactions.length === 0 ? (
                                <Box display="flex" alignItems="center" justifyContent="center" py={4}>
                                    <Typography color="text.secondary">No related transactions found</Typography>
                                </Box>
                            ) : relatedTransactions.map((row: any, i: number) => {
                                const clickable = !!row.guid && row.guid !== transactionId;
                                const color = row.type === 'Provision' ? 'var(--color-provision-text)' : 'var(--color-default-text)';
                                return (
                                    <Box
                                        key={row.guid || i}
                                        onClick={() => clickable && setSelectedLinkedTransactionId(row.guid)}
                                        sx={{ p: 1.25, borderRadius: 1, border: '1px solid #e0e0e0', background: 'var(--color-container-background)', display: 'flex', flexDirection: 'column', gap: 0.5, cursor: clickable ? 'pointer' : 'default' }}
                                    >
                                        <Box display="flex" justifyContent="space-between">
                                            <Typography sx={{ fontSize: 14, fontWeight: 600, color }}>{formatDate(new Date(row.date))}</Typography>
                                            <Typography sx={{ fontSize: 14, fontWeight: 700, color }}>
                                                {formatAmount?.(row.type === 'Provision' && row.originalAmount !== undefined ? row.originalAmount : row.amount)}
                                            </Typography>
                                        </Box>
                                        <Typography sx={{ fontSize: 13, color }}>{row.description}</Typography>
                                        <Box display="flex" gap={2}>
                                            <Typography sx={{ fontSize: 11, color: '#707070' }}>Bank: {row.bank}</Typography>
                                            <Typography sx={{ fontSize: 11, color: '#707070' }}>Category: {row.category}</Typography>
                                        </Box>
                                    </Box>
                                );
                            })
                        )}

                        {tabValue === 2 && (
                            fetchPatternAnalysis.isLoading ? (
                                <Box display="flex" flexDirection="column" alignItems="center" justifyContent="center" gap={2} py={4}>
                                    <CircularProgress size={40} />
                                    <Typography color="text.secondary">Loading pattern analysis...</Typography>
                                </Box>
                            ) : !patternAnalysis ? (
                                <Box display="flex" flexDirection="column" alignItems="center" justifyContent="center" gap={1} py={4}>
                                    <Typography color="text.secondary">No pattern analysis recorded</Typography>
                                    <Typography sx={{ fontSize: 12, color: 'var(--color-text-muted)', textAlign: 'center' }}>
                                        This provision wasn't created via "Provision This", so there's no source/matching transaction analysis to show.
                                    </Typography>
                                </Box>
                            ) : (
                                <>
                                    <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1.5, mb: 1 }}>
                                        <FieldBlock label="Interval" value={patternAnalysis.interval || '-'} />
                                        <FieldBlock label="Median Days" value={patternAnalysis.medianDays ?? '-'} />
                                        <FieldBlock label="Matched Range (days)" value={patternAnalysis.matchedRange ? `${patternAnalysis.matchedRange[0]} - ${patternAnalysis.matchedRange[1]}` : '-'} />
                                        {patternAnalysis.usedTolerancePercent != null && (
                                            <FieldBlock label="Tolerance Used" value={`${patternAnalysis.usedTolerancePercent}%`} />
                                        )}
                                    </Box>
                                    {patternAnalysisSourceTransaction && (
                                        <>
                                            <Typography sx={{ fontSize: 13, fontWeight: 700 }}>Source Transaction</Typography>
                                            <Box sx={{ p: 1.25, borderRadius: 1, border: '1px solid #e0e0e0', background: 'var(--color-container-background)', display: 'flex', justifyContent: 'space-between' }}>
                                                <Box>
                                                    <Typography sx={{ fontSize: 13, fontWeight: 600 }}>{formatDate(new Date(patternAnalysisSourceTransaction.date))}</Typography>
                                                    <Typography sx={{ fontSize: 12 }}>{patternAnalysisSourceTransaction.description}</Typography>
                                                </Box>
                                                <Typography sx={{ fontSize: 13, fontWeight: 700 }}>{formatAmount?.(patternAnalysisSourceTransaction.amount)}</Typography>
                                            </Box>
                                        </>
                                    )}
                                    <Typography sx={{ fontSize: 13, fontWeight: 700 }}>Matching Transactions</Typography>
                                    {patternAnalysisMatches.length === 0 ? (
                                        <Typography sx={{ fontSize: 12, color: 'var(--color-text-muted)' }}>No matches were recorded.</Typography>
                                    ) : patternAnalysisMatches
                                        .slice()
                                        .sort((a: any, b: any) => new Date(b.date).getTime() - new Date(a.date).getTime())
                                        .map((row: any, i: number) => (
                                            <Box key={i} sx={{ p: 1.25, borderRadius: 1, border: '1px solid #e0e0e0', background: 'var(--color-container-background)', display: 'flex', justifyContent: 'space-between' }}>
                                                <Box>
                                                    <Typography sx={{ fontSize: 13, fontWeight: 600 }}>{formatDate(new Date(row.date))}</Typography>
                                                    <Typography sx={{ fontSize: 12 }}>{row.description}</Typography>
                                                </Box>
                                                <Typography sx={{ fontSize: 13, fontWeight: 700 }}>{formatAmount?.(row.amount)}</Typography>
                                            </Box>
                                        ))}
                                </>
                            )
                        )}
                    </Box>
                </Box>
            )}

            {!fetch.isLoading && (
                <Box sx={{ borderTop: '1px solid #e0e0e0', p: 1.5, display: 'flex', flexDirection: 'column', gap: 1, position: 'sticky', bottom: 0, background: 'var(--color-page-background)' }}>
                    {data?.transaction?.type === 'real' && tabValue === 0 && (
                        <Typography sx={{ fontSize: 13, fontWeight: 600, textAlign: 'center' }}>
                            Remaining Balance: {formatAmount?.(data?.transaction?.amount - (data?.linkedTransactions || []).reduce((sum: number, trx: any) => sum + trx.amount, 0))}
                        </Typography>
                    )}
                    <Box display="flex" alignItems="center" justifyContent="center">
                        <TextToggle
                            checked={isLinkable}
                            size="large"
                            onChange={onToggleStatus}
                            disabled={fetch.isLoading}
                            text={!isLinkable ? "CLOSED" : "OPEN"}
                        />
                    </Box>
                    {!isUnlinked && (
                        <Box>
                            <Button
                                onClick={() => setOpenModal('link')}
                                disabled={linkButtonDisabled}
                                fullWidth
                                variant="outlined"
                                sx={{ display: 'flex', gap: 1 }}
                            >
                                <Link size={16} />
                                {data?.transaction?.type === 'provision' ? "Link this provision to a transaction" : "Link this transaction to a provision"}
                            </Button>
                            {linkTooltipTitle && (
                                <Typography sx={{ fontSize: 11, color: 'var(--color-text-muted)', textAlign: 'center', mt: 0.5 }}>{linkTooltipTitle}</Typography>
                            )}
                        </Box>
                    )}
                    {data?.transaction?.type === 'real' && can('transactions.addProvision', 'create') && (
                        <Box>
                            <Button
                                onClick={() => {
                                    const { url, method } = buildApiRequestConfig(routes.provisionTemplate.analyzePattern, [{ param: 'transactionGuid', value: transactionId || '' }]);
                                    setAnalyzePattern({ url, method });
                                    setProvisionThisData({
                                        date: adjustStartDate(data.transaction.date),
                                        amount: data.transaction.amount,
                                        description: data.transaction.description
                                    });
                                }}
                                disabled={provisionThisDisabled}
                                variant="contained"
                                fullWidth
                                sx={{ backgroundColor: 'var(--color-primary)', '&:hover': { backgroundColor: 'var(--color-primary)' } }}
                            >
                                Provision This
                            </Button>
                            {provisionThisTooltipTitle && (
                                <Typography sx={{ fontSize: 11, color: 'var(--color-text-muted)', textAlign: 'center', mt: 0.5 }}>{provisionThisTooltipTitle}</Typography>
                            )}
                        </Box>
                    )}
                </Box>
            )}
        </BottomSheetDialog>
    );
};

export default TransactionDetailMobile;
